import { apiFetch } from "@shared/api-client";
import type { TechRiderRequirements } from "./tech-rider-types";
import { attendanceBands, type PackageFormData } from "./types";
import { type InquiryProfileConfig } from "./inquiry-profile-schema";
import { activeInquiryFormData } from "./profile-state";

/**
 * Single owner of the inquiry submission (#334 item 2): every rule about
 * what rides the multipart payload to POST /api/inquiry lives here —
 * field stringification, the skip-absent rule, the tech-rider file, and
 * the pre-parsed rider JSON.
 * The wizard hands over its state and maps the outcome to SubmitState;
 * nothing about the wire format leaks into the component.
 *
 * Error contract is the SPA-wide apiFetch idiom: non-2xx throws ApiError
 * carrying the server `{error}` message (or `Request failed (N)` when the
 * body is some other shape); network-level failures propagate as the raw
 * fetch error. POSTs are never retried.
 */
export interface InquirySubmission {
  profile: InquiryProfileConfig;
  formData: Partial<PackageFormData>;
  /** Validated tech-rider upload, attached verbatim for the operator. It is
   *  also the parse source for autofill; here it is simply the first stored
   *  document. */
  techRiderFile: File | null;
  /** Extra documents the customer attached (#168) — stored for the operator
   *  alongside the tech rider, never parsed. */
  additionalDocuments?: File[];
  /** Gemini parse result when the background parse finished in time —
   *  lets the worker skip re-parsing the rider. */
  parsedTechRider: TechRiderRequirements | null;
  /** Stable for the lifetime of one form draft; replaying the same POST is safe. */
  submissionKey?: string;
}

export interface QuickInquiryDraft {
	contactName: string;
	requestSummary: string;
	contactEmail: string;
	eventName: string;
	eventDate: string;
	location: string;
}

export interface QuickInquirySubmission {
	profile: InquiryProfileConfig;
	draft: QuickInquiryDraft;
	submissionKey?: string;
}

/** One selected catalogue row on the dry-hire path (#136). */
export interface DryHireLine {
	equipmentId: string;
	quantity: number;
}

export interface DryHireDraft {
	contactName: string;
	contactEmail: string;
	contactPhone: string;
	/** Hire window, ISO dates. Rides the wire as eventDate/eventEndDate —
	 *  the backend maps those to the booking window for every path. */
	startDate: string;
	endDate: string;
	lines: DryHireLine[];
}

export interface DryHireSubmission {
	profile: InquiryProfileConfig;
	draft: DryHireDraft;
	submissionKey?: string;
}

/** UUID-shaped idempotency key, including browsers without randomUUID. */
export function createInquirySubmissionKey(): string {
	if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
	const bytes = new Uint8Array(16);
	if (globalThis.crypto?.getRandomValues) {
		globalThis.crypto.getRandomValues(bytes);
	} else {
		for (let index = 0; index < bytes.length; index += 1) {
			bytes[index] = Math.floor(Math.random() * 256);
		}
	}
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Attendance escape hatch: an exact number always wins; otherwise a band
 * maps to its representative number, which the AI prompt's setup/pack-down
 * defaults read. "unsure" carries no representative — attendance is simply
 * omitted (the backend treats it as optional). The raw band value also
 * rides along for the operator. Returns a copy; the estimate sends the
 * same resolution the submission does.
 */
export function withAttendanceFromBand(
  formData: Partial<PackageFormData>,
): Partial<PackageFormData> {
  const out = { ...formData };
  if (out.attendance === undefined && out.attendanceBand) {
    const band = attendanceBands.find(
      ({ value }) => value === out.attendanceBand,
    );
    if (band?.representative !== undefined) {
      out.attendance = band.representative;
    }
  }
  return out;
}

/**
 * Build the multipart body for POST /api/inquiry.
 *
 * - Absent answers (null/undefined) are OMITTED, not sent as "" — the
 *   backend defaults unanswered booleans to the worst-case gear list.
 * - Objects are JSON-stringified; primitives are stringified.
 *
 * Exported for tests; production callers hand it to useInquirySubmit.
 */
export function buildInquiryFormData({
  profile,
  formData,
  techRiderFile,
  additionalDocuments,
  parsedTechRider,
	submissionKey,
}: InquirySubmission): FormData {
  const fd = new FormData();
  fd.append("profileKey", profile.key);
	fd.append("inquiryPath", "full_brief");
	if (submissionKey) fd.append("submissionKey", submissionKey);
	const activeFormData = withAttendanceFromBand(
		activeInquiryFormData(profile, formData, techRiderFile !== null),
	);
  for (const [key, value] of Object.entries(activeFormData)) {
    // Pack answers ride as one JSON field below, not as flat entries.
    if (key === "profileAnswers") continue;
    if (value === null || value === undefined) continue;
    if (typeof value === "object") {
      fd.append(key, JSON.stringify(value));
      continue;
    }
    fd.append(key, String(value));
  }
  if (
    activeFormData.profileAnswers &&
    Object.keys(activeFormData.profileAnswers).length > 0
  ) {
    fd.append("profileAnswersJson", JSON.stringify(activeFormData.profileAnswers));
  }

  // Documents (#168): the parsed tech rider is stored as the first document,
  // followed by any extra attachments. All ride the same `documents` field —
  // the backend persists each and mirrors the first into techRiderDriveFileId.
  if (techRiderFile) {
    fd.append("documents", techRiderFile);
  }
  for (const doc of additionalDocuments ?? []) {
    fd.append("documents", doc);
  }
  if (parsedTechRider) {
    fd.append("parsedTechRiderJson", JSON.stringify(parsedTechRider));
  }

  return fd;
}

export function buildQuickInquiryFormData({
	profile,
	draft,
	submissionKey,
}: QuickInquirySubmission): FormData {
	const fd = new FormData();
	fd.append("profileKey", profile.key);
	fd.append("inquiryPath", "quick_enquiry");
	if (submissionKey) fd.append("submissionKey", submissionKey);
	fd.append("contactName", draft.contactName.trim());
	fd.append("requestSummary", draft.requestSummary.trim());
	fd.append("contactEmail", draft.contactEmail.trim());
	if (draft.eventName.trim()) fd.append("eventName", draft.eventName.trim());
	if (draft.eventDate) fd.append("eventDate", draft.eventDate);
	if (draft.location.trim()) fd.append("location", draft.location.trim());
	return fd;
}

export function buildDryHireInquiryFormData({
	profile,
	draft,
	submissionKey,
}: DryHireSubmission): FormData {
	const fd = new FormData();
	fd.append("profileKey", profile.key);
	fd.append("inquiryPath", "dry_hire");
	if (submissionKey) fd.append("submissionKey", submissionKey);
	fd.append("contactName", draft.contactName.trim());
	fd.append("contactEmail", draft.contactEmail.trim());
	if (draft.contactPhone.trim()) {
		fd.append("contactPhone", draft.contactPhone.trim());
	}
	fd.append("eventDate", draft.startDate);
	if (draft.endDate) fd.append("eventEndDate", draft.endDate);
	fd.append("dryHireLinesJson", JSON.stringify(draft.lines));
	return fd;
}

/** What POST /api/inquiry returns: statusToken unlocks the read-only
 *  status poll (GET /api/inquiry/status) on the confirmation screen. */
export interface InquirySubmitResult {
	statusToken?: string;
}

/** POST one built submission to /api/inquiry — every path's body is one of
 *  the builders above. Resolves on 2xx; throws ApiError / network errors
 *  per the apiFetch contract documented above. */
export async function postInquiry(body: FormData): Promise<InquirySubmitResult> {
	const res = await apiFetch<{ success: boolean; statusToken?: string }>(
		"/api/inquiry",
		{ method: "POST", body },
	);
	return { statusToken: res.statusToken };
}
